import type {
  IFdApplicationType,
  IFdOperatingCost,
  IFdProductionCost,
  IFdProductionPlan,
} from 'constants/interfaces';
import {
  lshFindSpendCategoryForApplicationCost,
  type IWalletCashflowUnmappedCost,
  type TWalletCashflowCostSource,
} from 'constants/walletCashflowCategoryMapping';
import type {ISpendCategory, IYearMonth} from 'constants/walletCashflowTypes';

import {lshParseOperatingCostMonthlyCosts} from '../steps/financials/financials.utils';

export const LSH_APPROVED_CASHFLOW_PERIOD_MONTHS = 18;

const MONTH_LABELS = [
  'jan',
  'feb',
  'mar',
  'apr',
  'may',
  'jun',
  'jul',
  'aug',
  'sep',
  'oct',
  'nov',
  'dec',
];

const MONTH_KEY_PATTERN =
  /^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)(\d{2})$/i;

export interface IApprovedCashflowDraftMonth extends IYearMonth {
  key: string;
  label: string;
}

export interface IApprovedCashflowDraftLine {
  spendCategoryId: string;
  spendCategoryLabel: string;
  allocatedAmount: number;
  source: TWalletCashflowCostSource;
  sourceLabel: string;
}

export interface IApprovedCashflowDraftLinesByMonth {
  key: string;
  period: IYearMonth;
  lines: IApprovedCashflowDraftLine[];
}

export interface IApprovedCashflowDraft {
  applicationId: string;
  periodStart?: string;
  periodEnd?: string;
  requiresPeriodSelection: boolean;
  totalAllocatedAmount: number;
  fundingAskTotal: number;
  reconciliationDifference: number;
  months: IApprovedCashflowDraftMonth[];
  linesByMonth: IApprovedCashflowDraftLinesByMonth[];
  unmappedItems: IWalletCashflowUnmappedCost[];
  warnings: string[];
}

export interface IBuildApprovedCashflowDraftInput {
  application: IFdApplicationType;
  spendCategories: ISpendCategory[];
  periodStart?: IYearMonth;
}

type ParsedMonthKey = {
  key: string;
  year: number;
  month: number;
  monthIndex: number;
  sortValue: number;
};

type DraftLineAccumulator = IApprovedCashflowDraftLine & {
  sourceLabels: Set<string>;
};

function lshRoundMoney(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

function lshToMoney(value: unknown): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? lshRoundMoney(parsed) : 0;
}

function lshParseMonthKey(value: string): ParsedMonthKey | null {
  const match = String(value || '')
    .toLowerCase()
    .match(MONTH_KEY_PATTERN);
  if (!match) return null;

  const monthIndex = MONTH_LABELS.indexOf(match[1].toLowerCase());
  if (monthIndex < 0) return null;

  const year = 2000 + Number(match[2]);
  return {
    key: `${MONTH_LABELS[monthIndex]}${String(year).slice(-2)}`,
    year,
    month: monthIndex + 1,
    monthIndex,
    sortValue: year * 12 + monthIndex,
  };
}

function lshMonthKeyFromPeriod(period: IYearMonth): string {
  return `${MONTH_LABELS[period.month - 1]}${String(period.year).slice(-2)}`;
}

function lshMonthLabel(month: IApprovedCashflowDraftMonth): string {
  return `${MONTH_LABELS[month.month - 1].toUpperCase()}${String(
    month.year,
  ).slice(-2)}`;
}

function lshDateOnly(year: number, month: number, day: number): string {
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(
    2,
    '0',
  )}`;
}

function lshEndOfMonthDateOnly(year: number, month: number): string {
  return lshDateOnly(year, month, new Date(year, month, 0).getDate());
}

function lshGenerateMonths(periodStart: IYearMonth) {
  return Array.from(
    {length: LSH_APPROVED_CASHFLOW_PERIOD_MONTHS},
    (_, index): IApprovedCashflowDraftMonth => {
      const zeroBasedStart = periodStart.month - 1;
      const year = periodStart.year + Math.floor((zeroBasedStart + index) / 12);
      const monthIndex = (zeroBasedStart + index) % 12;
      const month = monthIndex + 1;
      const draftMonth = {
        year,
        month,
        key: lshMonthKeyFromPeriod({year, month}),
        label: '',
      };

      return {
        ...draftMonth,
        label: lshMonthLabel(draftMonth),
      };
    },
  );
}

function lshInferPeriodStartFromOperatingCosts(
  operatingCosts: IFdOperatingCost[],
): IYearMonth | null {
  const parsedMonthKeys = operatingCosts
    .flatMap((cost) =>
      Object.keys(
        lshParseOperatingCostMonthlyCosts(cost.monthlyCosts).monthlyValues,
      ),
    )
    .map(lshParseMonthKey)
    .filter((month): month is ParsedMonthKey => Boolean(month))
    .sort((left, right) => left.sortValue - right.sortValue);

  const first = parsedMonthKeys[0];
  return first ? {year: first.year, month: first.month} : null;
}

function lshInferPeriodStartFromProductionCosts(
  productionCosts: IFdProductionCost[],
  productionPlans: IFdProductionPlan[],
): IYearMonth | null {
  const productionMonths = productionCosts
    .map((productionCost) => {
      const linkedPlan = lshFindProductionPlanForCost(
        productionCost,
        productionPlans,
      );
      const plantingDate =
        productionCost.plantingDate || linkedPlan?.plantingDate;
      const parsedDate = plantingDate ? new Date(plantingDate) : null;
      if (!parsedDate || Number.isNaN(parsedDate.getTime())) return null;

      return {
        year: parsedDate.getFullYear(),
        month: parsedDate.getMonth() + 1,
        sortValue: parsedDate.getFullYear() * 12 + parsedDate.getMonth(),
      };
    })
    .filter(
      (
        productionMonth,
      ): productionMonth is {year: number; month: number; sortValue: number} =>
        Boolean(productionMonth),
    )
    .sort((left, right) => left.sortValue - right.sortValue);

  const first = productionMonths[0];
  return first ? {year: first.year, month: first.month} : null;
}

function lshInferPeriodStartFromFundingAsk(
  application: IFdApplicationType,
): IYearMonth | null {
  const year = Number(application.fundingAsk?.expectedFundingYear);
  const month = Number(application.fundingAsk?.expectedFundingMonth);

  if (!Number.isFinite(year) || !Number.isFinite(month)) return null;
  if (month < 1 || month > 12) return null;

  return {year, month};
}

function lshFindProductionPlanForCost(
  productionCost: IFdProductionCost,
  productionPlans: IFdProductionPlan[],
): IFdProductionPlan | undefined {
  const cropId = productionCost.crop?.id ? String(productionCost.crop.id) : '';
  if (!cropId) return undefined;
  return productionPlans.find((plan) => String(plan.crop?.id || '') === cropId);
}

function lshSplitAmountAcrossMonths(
  amount: number,
  monthKeys: string[],
): Array<{key: string; amount: number}> {
  if (amount <= 0 || monthKeys.length === 0) return [];

  const baseAmount = lshRoundMoney(amount / monthKeys.length);
  let remaining = lshRoundMoney(amount);

  return monthKeys.map((key, index) => {
    const isLast = index === monthKeys.length - 1;
    const monthlyAmount = isLast ? remaining : baseAmount;
    remaining = lshRoundMoney(remaining - monthlyAmount);
    return {key, amount: monthlyAmount};
  });
}

function lshProductionMonthKeys(
  plantingDate: string | null | undefined,
  months: IApprovedCashflowDraftMonth[],
): string[] {
  const parsedDate = plantingDate ? new Date(plantingDate) : null;
  const plantingMonthIndex =
    parsedDate && !Number.isNaN(parsedDate.getTime())
      ? parsedDate.getMonth()
      : null;

  const startIndex =
    plantingMonthIndex === null
      ? 0
      : Math.max(
          0,
          months.findIndex((month) => month.month - 1 === plantingMonthIndex),
        );

  return months.slice(startIndex, startIndex + 3).map((month) => month.key);
}

function lshBuildMonthLineLookup() {
  return new Map<string, DraftLineAccumulator>();
}

function lshAddDraftLine(
  lineLookup: Map<string, DraftLineAccumulator>,
  monthKey: string,
  category: ISpendCategory,
  amount: number,
  source: TWalletCashflowCostSource,
  sourceLabel: string,
) {
  if (amount <= 0) return;
  const lookupKey = `${monthKey}:${category.id}`;
  const existing = lineLookup.get(lookupKey);

  if (existing) {
    existing.allocatedAmount = lshRoundMoney(existing.allocatedAmount + amount);
    existing.source =
      existing.source === source ? existing.source : ('unknown' as const);
    existing.sourceLabels.add(sourceLabel);
    existing.sourceLabel = Array.from(existing.sourceLabels).join(', ');
    return;
  }

  lineLookup.set(lookupKey, {
    spendCategoryId: category.id,
    spendCategoryLabel: category.label,
    allocatedAmount: lshRoundMoney(amount),
    source,
    sourceLabel,
    sourceLabels: new Set([sourceLabel]),
  });
}

function lshBuildLinesByMonth(
  months: IApprovedCashflowDraftMonth[],
  lineLookup: Map<string, DraftLineAccumulator>,
): IApprovedCashflowDraftLinesByMonth[] {
  return months.map((month) => ({
    key: month.key,
    period: {year: month.year, month: month.month},
    lines: Array.from(lineLookup.entries())
      .filter(([key]) => key.startsWith(`${month.key}:`))
      .map(([, line]) => ({
        spendCategoryId: line.spendCategoryId,
        spendCategoryLabel: line.spendCategoryLabel,
        allocatedAmount: line.allocatedAmount,
        source: line.source,
        sourceLabel: line.sourceLabel,
      }))
      .sort((left, right) =>
        left.spendCategoryLabel.localeCompare(right.spendCategoryLabel),
      ),
  }));
}

function lshAddUnmappedItem(
  unmappedItems: IWalletCashflowUnmappedCost[],
  label: string,
  amount: number,
  source: TWalletCashflowCostSource,
) {
  if (amount <= 0) return;
  unmappedItems.push({label, amount: lshRoundMoney(amount), source});
}

function lshBuildEmptyDraft(
  application: IFdApplicationType,
  requiresPeriodSelection: boolean,
): IApprovedCashflowDraft {
  const fundingAskTotal = lshRoundMoney(
    (application.fundingAsk?.fundingRequestCosts || []).reduce(
      (sum, item) => sum + lshToMoney(item.cost),
      0,
    ),
  );

  return {
    applicationId: String(application.id),
    requiresPeriodSelection,
    totalAllocatedAmount: 0,
    fundingAskTotal,
    reconciliationDifference: lshRoundMoney(0 - fundingAskTotal),
    months: [],
    linesByMonth: [],
    unmappedItems: [],
    warnings: requiresPeriodSelection
      ? ['Select a cashflow period before publishing approved budgets.']
      : [],
  };
}

export function lshBuildApprovedCashflowDraft(
  input: IBuildApprovedCashflowDraftInput,
): IApprovedCashflowDraft {
  const {application, spendCategories, periodStart} = input;
  const operatingCosts = application.operatingCosts || [];
  const productionCosts = application.productionCosts || [];
  const productionPlans = application.productionPlans || [];
  const inferredPeriodStart =
    periodStart ||
    lshInferPeriodStartFromOperatingCosts(operatingCosts) ||
    lshInferPeriodStartFromProductionCosts(productionCosts, productionPlans) ||
    lshInferPeriodStartFromFundingAsk(application);

  if (!inferredPeriodStart) {
    return lshBuildEmptyDraft(application, true);
  }

  const months = lshGenerateMonths(inferredPeriodStart);
  const monthByKey = new Map(months.map((month) => [month.key, month]));
  const lineLookup = lshBuildMonthLineLookup();
  const unmappedItems: IWalletCashflowUnmappedCost[] = [];
  const warnings: string[] = [];

  productionCosts.forEach((productionCost) => {
    const linkedPlan = lshFindProductionPlanForCost(
      productionCost,
      productionPlans,
    );
    const monthKeys = lshProductionMonthKeys(
      productionCost.plantingDate || linkedPlan?.plantingDate,
      months,
    );

    const productionItems: Array<{
      label: string;
      amount: number;
      source: TWalletCashflowCostSource;
    }> = [
      {
        label: 'Seed',
        amount: lshToMoney(productionCost.seed),
        source: 'production.seed',
      },
      {
        label: 'Fertiliser',
        amount: lshToMoney(productionCost.fertiliser),
        source: 'production.fertiliser',
      },
      {
        label: 'Chemical',
        amount: lshToMoney(productionCost.chemical),
        source: 'production.chemical',
      },
    ];

    productionItems.forEach((item) => {
      const category = lshFindSpendCategoryForApplicationCost(
        item.label,
        spendCategories,
      );

      if (!category) {
        lshAddUnmappedItem(unmappedItems, item.label, item.amount, item.source);
        return;
      }

      lshSplitAmountAcrossMonths(item.amount, monthKeys).forEach((split) => {
        lshAddDraftLine(
          lineLookup,
          split.key,
          category,
          split.amount,
          item.source,
          item.label,
        );
      });
    });
  });

  operatingCosts.forEach((operatingCost) => {
    const label = operatingCost.expense?.name || 'Operating expense';
    const source = 'operating' as const;
    const category = lshFindSpendCategoryForApplicationCost(
      label,
      spendCategories,
    );
    const parsed = lshParseOperatingCostMonthlyCosts(
      operatingCost.monthlyCosts,
    ).monthlyValues;
    const total = Object.values(parsed).reduce(
      (sum, amount) => sum + lshToMoney(amount),
      0,
    );

    if (!category) {
      lshAddUnmappedItem(unmappedItems, label, total, source);
      return;
    }

    Object.entries(parsed).forEach(([monthKey, rawAmount]) => {
      const parsedMonth = lshParseMonthKey(monthKey);
      const amount = lshToMoney(rawAmount);
      if (!amount) return;

      if (!parsedMonth || !monthByKey.has(parsedMonth.key)) {
        warnings.push(
          `${label} has a saved amount outside the approved cashflow period: ${monthKey}`,
        );
        return;
      }

      lshAddDraftLine(
        lineLookup,
        parsedMonth.key,
        category,
        amount,
        source,
        label,
      );
    });
  });

  const linesByMonth = lshBuildLinesByMonth(months, lineLookup);
  const totalAllocatedAmount = lshRoundMoney(
    linesByMonth.reduce(
      (monthSum, month) =>
        monthSum +
        month.lines.reduce(
          (lineSum, line) => lineSum + line.allocatedAmount,
          0,
        ),
      0,
    ),
  );
  const fundingAskTotal = lshRoundMoney(
    (application.fundingAsk?.fundingRequestCosts || []).reduce(
      (sum, item) => sum + lshToMoney(item.cost),
      0,
    ),
  );
  const lastMonth = months[months.length - 1];

  return {
    applicationId: String(application.id),
    periodStart: lshDateOnly(
      inferredPeriodStart.year,
      inferredPeriodStart.month,
      1,
    ),
    periodEnd: lastMonth
      ? lshEndOfMonthDateOnly(lastMonth.year, lastMonth.month)
      : undefined,
    requiresPeriodSelection: false,
    totalAllocatedAmount,
    fundingAskTotal,
    reconciliationDifference: lshRoundMoney(
      totalAllocatedAmount - fundingAskTotal,
    ),
    months,
    linesByMonth,
    unmappedItems,
    warnings,
  };
}
